import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import RegistrationAgeWheel from "../../pages/auth/RegistrationAgeWheel";
import {
  isValidRegistrationAge,
} from "../../pages/auth/registrationAge";

const dismissalKey = (userId) => `vg-age-prompt-dismissed:${userId}`;

export default function ReportedAgePromptModal({ userId, blocked = false, onPriorityChange }) {
  const { t } = useTranslation("auth");
  const [eligible, setEligible] = useState(false);
  const [age, setAge] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setEligible(false);
    setAge("");
    if (!userId || sessionStorage.getItem(dismissalKey(userId))) return undefined;
    let cancelled = false;
    api.get("/accounts/profile/", { meta: { suppressGlobalError: true } })
      .then(({ data }) => {
        if (!cancelled) setEligible(data?.age_reported == null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    onPriorityChange?.(Boolean(userId && eligible && !blocked));
  }, [blocked, eligible, onPriorityChange, userId]);

  const close = () => {
    if (userId) sessionStorage.setItem(dismissalKey(userId), "1");
    setEligible(false);
    setError("");
  };

  const parsedAge = Number(age);
  const validAge = isValidRegistrationAge(age);
  const save = async () => {
    if (!validAge || saving) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch("/accounts/profile/", { age_reported: parsedAge }, { meta: { suppressGlobalError: true } });
      if (data?.errors || data?.error) throw new Error(data?.errors?.age_reported?.[0] || data?.error);
      close();
    } catch (saveError) {
      setError(saveError?.response?.data?.errors?.age_reported?.[0] || saveError?.message || t("age_prompt.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModalShell
      open={Boolean(userId && eligible && !blocked)}
      onClose={close}
      title={t("age_prompt.title")}
      description={t("age_prompt.description")}
      closeAriaLabel={t("age_prompt.close")}
      footer={
        <AppModalButton onClick={save} disabled={!validAge} loading={saving}>{t("age_prompt.save")}</AppModalButton>
      }
    >
      <div className="flex flex-col items-center pb-2">
        <RegistrationAgeWheel
          value={age}
          onChange={(nextAge) => { setAge(nextAge); setError(""); }}
          label={t("registration_flow.age.input")}
        />
        {error ? <p role="alert" className="mt-3 text-[13px] text-red-300">{error}</p> : null}
      </div>
    </AppModalShell>
  );
}
